import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import Filter from "pages/purchase/filter"; // reuse filter komponen yang sama
import nProgress from "nprogress";
import { useAuth } from "contexts/auth-context";
import { Pagination, Transition } from "components";
import { lang } from "config";
import { Pencil, Plus } from "components/icons/outline";
import { useResultSet } from "repositories/db-repository";
import Delete from "./delete";     // pastikan ada versi Return
import Refresh from "./refresh";   // pastikan ada versi Return
import { getBranchList, getParameter } from "helpers/general-helper";

export default function List() {
  const { user, can, currentBranch } = useAuth();

  const { title, transition, setTransition } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchInput = useRef();

  const page = searchParams.get("page");
  const search = searchParams.get("search");
  const status = searchParams.get("status");

  const branchSelection = getBranchList();
  const branch_id_list = () => {
    const ids = [];
    if (Array.isArray(branchSelection)) {
      branchSelection.forEach((b) => ids.push(b.id));
      return ids;
    }
    return undefined;
  };

  // ==== DATA RETURN HEADER (utama) ====
  // gunakan endpoint baru untuk return; backend: /return_header
  const { data: result_set, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_REMAX_PS_API_URL}`,
    `return_header`,
    {
      outlet_id: currentBranch?.id,
      outlet_group_id: getParameter("OUTLET_GROUP_ID"),
      branch_list: JSON.stringify(branch_id_list()),
      page,
      record_per_page: 5,
      search,
      // beberapa backend kamu mungkin pakai latest_flow_detail_id (seperti purchase),
      // Return kita fallback ke flow_detail_id kalau field latest tidak ada.
      latest_flow_detail_id: status,
      flow_detail_id: status,
    }
  );

  // ==== LIST FLOW (status filter) – reuse yang sama ====
  const { data: list_flow } = useResultSet(`${process.env.REACT_APP_REMAX_PS_API_URL}`, `list_flow`, {});

  useEffect(() => {
    window.history.replaceState({}, document.title);
    if (transition.reverse) setTransition({ type: "slide", reverse: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // autofocus ke search tiap search berubah (mirror dengan milikmu)
    searchInput.current?.focus();
  }, [search]);

  useEffect(() => {
    if (search === "") {
      navigate(``, { replace: true });
    }
    if (isLoading) nProgress.start();
    else nProgress.done();
  }, [result_set, isLoading, navigate, search]);

  // ==== Filter handlers ====
  const updateFilter = (value) => setSearchParams(value);
  const removeFilter = () => setSearchParams({});

  const generatePaginationParam = () => {
    let tmp = "";
    if (status) tmp += `&status=${status}`;
    if (searchParams.has("search")) {
      const s = searchParams.get("search");
      tmp += `&search=${s}`;
    }
    return tmp;
  };

  // ==== Helper render fallback jika header/dual tidak tersedia ====
  const hasDual = Array.isArray(result_set?.dual) && result_set.dual.length > 0;
  const hasHeader = Array.isArray(result_set?.header) && result_set.header.length > 0;
  const rows = hasDual ? result_set.dual : (Array.isArray(result_set?.data) ? result_set.data : []);
  const emptyState = !isLoading && ((hasDual && result_set.dual.length === 0) || (!hasDual && (!result_set?.data || result_set.data.length === 0)));

  return (
    <Transition type="fade">
      <div className="pb-4 space-y-6 lg:pb-8">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center sm:space-x-2">
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <circle cx={10} cy={10} r={7}></circle>
                  <line x1={21} y1={21} x2={15} y2={15}></line>
                </svg>
              </div>
              <input
                ref={searchInput}
                onChange={(e) => setSearchParams({ search: e.target.value })}
                value={search ?? ""}
                type="text"
                placeholder={`${lang.search} Return`}
                autoComplete="off"
                className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
            </div>

            {/* Reuse filter; passing status list_flow yg kamu punya */}
            <Filter
              list_flow={list_flow}
              onSubmit={updateFilter}
              onRemove={removeFilter}
              data={Object.fromEntries(Object.entries({ status }).filter(([_, v]) => v != null))}
            />
          </div>

          <div className="flex items-center space-x-2">
            {can("return.create") && (
              <div>
                <Link
                  to="create"
                  onClick={() => {
                    setTransition({ type: "slide", reverse: true });
                  }}
                  state={{ back: title, from: location?.pathname, transition: { type: "slide" } }}
                  className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90"
                >
                  <Plus className="w-4 h-4" stroke={1.5} />
                  <span>{lang.create}</span>
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
            <thead className="bg-neutral-50 rounded-t-3xl">
              <tr>
                {/* Jika backend sudah supply header/dual (mirror purchase) → pakai indeks 1/3/5/6/7/8 */}
                {hasHeader &&
                  result_set.header.map((h, idx) => {
                    if ([1, 3, 5, 6, 7, 8].includes(idx)) {
                      return (
                        <th key={idx} scope="col" className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                          {h}
                        </th>
                      );
                    }
                    return null;
                  })}
                {/* Fallback header manual bila header kosong */}
                {!hasHeader && (
                  <>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Return No</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Supplier</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Warehouse</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Return Type</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Amount</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Nett</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Created At</th>
                  </>
                )}
                {(hasHeader || !hasHeader) && <th scope="col" className="relative px-1 py-3"><span className="sr-only">{lang.action}</span></th>}
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-neutral-200">
              {/* Loading */}
              {isLoading && (
                <tr className="text-center">
                  <td colSpan="10" className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.loading_data}
                  </td>
                </tr>
              )}

              {/* Empty states */}
              {emptyState && !search && (
                <tr className="text-center">
                  <td colSpan="10" className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_data}
                  </td>
                </tr>
              )}
              {emptyState && search && (
                <tr className="text-center">
                  <td colSpan="10" className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_result}
                  </td>
                </tr>
              )}

              {/* Render jika backend supply dual */}
              {hasDual &&
                rows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row[1]?.length > 0 &&
                      row[1].map((cell, cIdx) => {
                        if ([1, 3, 5, 6, 7, 8].includes(cIdx)) {
                          const text = String(cell ?? "");
                          return (
                            <td key={cIdx} className="px-1 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                              {text.length > 50 ? text.substring(0, 50) + "..." : text}
                            </td>
                          );
                        }
                        return null;
                      })}
                    <td className="px-1 py-4 text-xs font-medium text-right whitespace-nowrap">
                      <div className="inline-flex items-center space-x-2">
                        {(can("return.edit") || can("return.approve") || can("return.close")) && (
                          <div className="space-x-2">
                            <Link
                              to={`${row[0].id}/edit`}
                              state={{ back: title, from: location?.pathname, transition: "slide" }}
                              className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90"
                            >
                              <Pencil className="w-6 h-6" />
                            </Link>
                            <Refresh data={row[0]} onSuccess={mutate} />
                          </div>
                        )}
                        {can("return.delete") && <Delete data={row[0]} onSuccess={mutate} />}
                      </div>
                    </td>
                  </tr>
                ))}

              {/* Fallback render jika backend tidak supply dual/header → pakai data mentah */}
              {!hasDual &&
                Array.isArray(rows) &&
                rows.map((h, i) => (
                  <tr key={h?.id ?? i}>
                    <td className="px-1 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">{h?.id ?? "-"}</td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">{h?.supplier_name ?? "-"}</td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">{h?.warehouse_name ?? "-"}</td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">{h?.return_type ?? "-"}</td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                      {h?.amount ? Intl.NumberFormat("id-Id", { style: "currency", currency: "idr" }).format(h.amount) : "-"}
                    </td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                      {h?.nett_amount ? Intl.NumberFormat("id-Id", { style: "currency", currency: "idr" }).format(h.nett_amount) : "-"}
                    </td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">{h?.flow_detail_id ?? "-"}</td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">{h?.created_at ?? "-"}</td>

                    <td className="px-1 py-4 text-xs font-medium text-right whitespace-nowrap">
                      <div className="inline-flex items-center space-x-2">
                        {(can("return.edit") || can("return.approve") || can("return.close")) && (
                          <div className="space-x-2">
                            <Link
                              to={`${h?.id}/edit`}
                              state={{ back: title, from: location?.pathname, transition: "slide" }}
                              className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90"
                            >
                              <Pencil className="w-6 h-6" />
                            </Link>
                            {/* <Refresh data={h} onSuccess={mutate} /> */}
                          </div>
                        )}
                        {can("return.delete") && <Delete data={h} onSuccess={mutate} />}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination
          params={generatePaginationParam()}
          links={result_set?.links}
          from={result_set?.from}
          to={result_set?.to}
          total={result_set?.total}
        />
      </div>
    </Transition>
  );
}
